import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import axios from 'axios'
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core'
import { AddCircleOutline, DeleteOutline } from '@material-ui/icons'
import { makeStyles } from '@material-ui/core/styles'
import {
  PortalLoadingCard,
  PortalMetricCard,
  PortalStateCard,
  formatPortalDateTime,
  usePortalTableStyles
} from './portalShared'

const useStyles = makeStyles((theme) => ({
  emptyCell: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4)
  },
  applicationAreaWrap: {
    display: 'flex',
    gap: theme.spacing(1),
    flexWrap: 'wrap'
  },
  chemistryBlock: {
    border: '1px solid var(--df-line)',
    borderRadius: 'var(--df-radius-md)',
    padding: theme.spacing(2),
    background: 'var(--df-surface)'
  },
  chemistryHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(1)
  },
  rateLabel: {
    fontWeight: 700,
    color: 'var(--df-ink)'
  }
}))

const APPLICATION_AREAS = ['Ornamental/Right-of-Way', 'Turf', 'Bare Ground']
const SEASON_OPTIONS = ['Warm', 'Cold', 'All']
const TYPE_OPTIONS = ['Pre Emergent', 'Post', 'Combo', 'All']
const RATE_UNITS = ['oz', 'gal']
const RATE_PERS = ['gal', '100gal', 'acre', '1000sqft']

const EMPTY_CHEMICAL = {
  chemicalId: '',
  name: '',
  spotRate: '',
  spotUnit: 'oz',
  spotPer: 'acre',
  broadcastRate: '',
  broadcastUnit: 'oz',
  broadcastPer: 'acre'
}

const EMPTY_FORM = {
  name: '',
  season: 'All',
  type: 'All',
  applicationAreas: [],
  calibrationRate: 20,
  chemicals: [{ ...EMPTY_CHEMICAL }]
}

const EMPTY_REQUEST_FORM = {
  name: '',
  manufacturer: '',
  epaRegistrationNumber: '',
  activeIngredient: '',
  formulation: '',
  sdsUrl: '',
  labelUrl: '',
  notes: ''
}

function getRequestStatusMeta (status) {
  if (status === 'approved') return { label: 'Approved', className: 'df-chip-ok' }
  if (status === 'rejected') return { label: 'Rejected', className: 'df-chip-danger' }
  return { label: 'Pending Review', className: 'df-chip-warn' }
}

function buildChemicalOptionLabel (chemical) {
  return chemical?.name || chemical?.altName || chemical?.description || 'Unnamed chemical'
}

function hydrateFormFromBlend (blend) {
  return {
    name: blend?.name || '',
    season: blend?.season || 'All',
    type: blend?.type || 'All',
    applicationAreas: Array.isArray(blend?.applicationAreas) ? blend.applicationAreas : [],
    calibrationRate: blend?.calibrationRate ?? 20,
    chemicals: Array.isArray(blend?.chemicals) && blend.chemicals.length
      ? blend.chemicals.map((chemical) => ({
        chemicalId: chemical?.chemicalId || '',
        name: chemical?.name || '',
        spotRate: chemical?.spot?.rate ?? '',
        spotUnit: chemical?.spot?.unit || 'oz',
        spotPer: chemical?.spot?.per || 'acre',
        broadcastRate: chemical?.broadcast?.rate ?? '',
        broadcastUnit: chemical?.broadcast?.unit || 'oz',
        broadcastPer: chemical?.broadcast?.per || 'acre'
      }))
      : [{ ...EMPTY_CHEMICAL }]
  }
}

function sanitizeChemicalRow (row, chemicals) {
  const selectedChemical = chemicals.find((chemical) => String(chemical._id) === String(row.chemicalId))
  const spotRate = row.spotRate === '' ? '' : Number(row.spotRate)
  const broadcastRate = row.broadcastRate === '' ? '' : Number(row.broadcastRate)

  const payload = {
    chemicalId: row.chemicalId,
    name: buildChemicalOptionLabel(selectedChemical) || row.name
  }

  if (row.spotRate !== '') {
    payload.spot = {
      rate: Number.isFinite(spotRate) ? spotRate : row.spotRate,
      unit: row.spotUnit,
      per: row.spotPer
    }
  }

  if (row.broadcastRate !== '') {
    payload.broadcast = {
      rate: Number.isFinite(broadcastRate) ? broadcastRate : row.broadcastRate,
      unit: row.broadcastUnit,
      per: row.broadcastPer
    }
  }

  return payload
}

function validateBlendForm (form, chemicals) {
  if (!form.name.trim()) {
    return 'Blend name is required.'
  }

  if (!Array.isArray(form.applicationAreas) || !form.applicationAreas.length) {
    return 'Select at least one application area.'
  }

  if (!Array.isArray(form.chemicals) || !form.chemicals.length) {
    return 'Add at least one chemical.'
  }

  for (const row of form.chemicals) {
    if (!row.chemicalId) {
      return 'Each chemical row must select a chemical.'
    }

    const hasSpot = row.spotRate !== ''
    const hasBroadcast = row.broadcastRate !== ''
    if (!hasSpot && !hasBroadcast) {
      return 'Each chemical row needs a spot or broadcast rate.'
    }

    if (!chemicals.find((chemical) => String(chemical._id) === String(row.chemicalId))) {
      return 'One of the selected chemicals is no longer available.'
    }
  }

  return ''
}

const SubcontractorBlends = ({ user }) => {
  const classes = useStyles()
  const shared = usePortalTableStyles()
  const isAdmin = user?.subcontractorProfile?.role === 'admin'
  const canCreate = Boolean(user?.subcontractorProfile?.isSubcontractor)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [submitError, setSubmitError] = useState('')
  const [items, setItems] = useState([])
  const [chemicals, setChemicals] = useState([])
  const [summary, setSummary] = useState({})
  const [requestSummary, setRequestSummary] = useState({})
  const [chemicalRequests, setChemicalRequests] = useState([])
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingBlend, setEditingBlend] = useState(null)
  const [form, setForm] = useState(EMPTY_FORM)
  const [requestDialogOpen, setRequestDialogOpen] = useState(false)
  const [requestForm, setRequestForm] = useState(EMPTY_REQUEST_FORM)
  const [requestSubmitError, setRequestSubmitError] = useState('')

  const loadBlends = useCallback(async () => {
    setLoading(true)
    setError('')

    try {
      const [blendResponse, requestResponse] = await Promise.all([
        axios.get('/api/subcontractor/blends', {
          params: {
            includeInactive: isAdmin ? 'true' : undefined
          }
        }),
        axios.get('/api/subcontractor/chemical-requests')
      ])
      setChemicals(blendResponse.data?.chemicals || [])
      setItems(blendResponse.data?.items || [])
      setSummary(blendResponse.data?.summary || {})
      setChemicalRequests(requestResponse.data?.items || [])
      setRequestSummary(requestResponse.data?.summary || {})
    } catch (requestError) {
      console.error('Failed to load subcontractor blends', requestError)
      setError(requestError?.response?.data?.message || 'Unable to load subcontractor blends right now.')
    } finally {
      setLoading(false)
    }
  }, [isAdmin])

  useEffect(() => {
    loadBlends()
  }, [loadBlends])

  const resetForm = () => {
    setForm({
      ...EMPTY_FORM,
      chemicals: [{ ...EMPTY_CHEMICAL }]
    })
    setEditingBlend(null)
    setSubmitError('')
  }

  const resetRequestForm = () => {
    setRequestForm(EMPTY_REQUEST_FORM)
    setRequestSubmitError('')
  }

  const openCreateDialog = () => {
    resetForm()
    setDialogOpen(true)
  }

  const openEditDialog = (blend) => {
    setEditingBlend(blend)
    setSubmitError('')
    setForm(hydrateFormFromBlend(blend))
    setDialogOpen(true)
  }

  const closeDialog = () => {
    if (saving) {
      return
    }
    setDialogOpen(false)
    resetForm()
  }

  const openRequestDialog = () => {
    resetRequestForm()
    setRequestDialogOpen(true)
  }

  const closeRequestDialog = () => {
    if (saving) {
      return
    }
    setRequestDialogOpen(false)
    resetRequestForm()
  }

  const handleApplicationAreaToggle = (area) => {
    setForm((current) => ({
      ...current,
      applicationAreas: current.applicationAreas.includes(area)
        ? current.applicationAreas.filter((item) => item !== area)
        : [...current.applicationAreas, area]
    }))
  }

  const addChemicalRow = () => {
    setForm((current) => ({
      ...current,
      chemicals: [...current.chemicals, { ...EMPTY_CHEMICAL }]
    }))
  }

  const removeChemicalRow = (index) => {
    setForm((current) => ({
      ...current,
      chemicals: current.chemicals.filter((_, rowIndex) => rowIndex !== index)
    }))
  }

  const updateChemicalRow = (index, patch) => {
    setForm((current) => ({
      ...current,
      chemicals: current.chemicals.map((row, rowIndex) => {
        if (rowIndex !== index) {
          return row
        }

        const nextRow = {
          ...row,
          ...patch
        }

        if (patch.chemicalId) {
          const selectedChemical = chemicals.find((chemical) => String(chemical._id) === String(patch.chemicalId))
          nextRow.name = buildChemicalOptionLabel(selectedChemical)
        }

        return nextRow
      })
    }))
  }

  const handleSubmit = async () => {
    const validationError = validateBlendForm(form, chemicals)
    if (validationError) {
      setSubmitError(validationError)
      return
    }

    const payload = {
      name: form.name.trim(),
      season: form.season,
      type: form.type,
      applicationAreas: form.applicationAreas,
      calibrationRate: Number(form.calibrationRate) || 20,
      chemicals: form.chemicals.map((row) => sanitizeChemicalRow(row, chemicals))
    }

    setSaving(true)
    setSubmitError('')

    try {
      if (editingBlend?._id) {
        await axios.put(`/api/subcontractor/blends/${editingBlend._id}`, payload)
      } else {
        await axios.post('/api/subcontractor/blends', payload)
      }

      setDialogOpen(false)
      resetForm()
      await loadBlends()
    } catch (requestError) {
      console.error('Failed to save subcontractor blend', requestError)
      setSubmitError(requestError?.response?.data?.message || 'Unable to save subcontractor blend right now.')
    } finally {
      setSaving(false)
    }
  }

  const handleDeactivate = async (blend) => {
    const confirmed = window.confirm(`Deactivate ${blend.name}? It will be hidden from active subcontractor blend lists.`)
    if (!confirmed) {
      return
    }

    try {
      await axios.delete(`/api/subcontractor/blends/${blend._id}`)
      await loadBlends()
    } catch (requestError) {
      console.error('Failed to deactivate subcontractor blend', requestError)
      setError(requestError?.response?.data?.message || 'Unable to deactivate that blend right now.')
    }
  }

  const handleActivate = async (blend) => {
    const confirmed = window.confirm(`Activate ${blend.name}? It will be available in active subcontractor blend lists.`)
    if (!confirmed) {
      return
    }

    try {
      await axios.put(`/api/subcontractor/blends/${blend._id}`, {
        name: blend.name,
        season: blend.season,
        type: blend.type,
        applicationAreas: blend.applicationAreas || [],
        calibrationRate: blend.calibrationRate,
        chemicals: (blend.chemicals || []).map((chemical) => ({
          chemicalId: chemical.chemicalId,
          name: chemical.name,
          ...(chemical.spot ? { spot: chemical.spot } : {}),
          ...(chemical.broadcast ? { broadcast: chemical.broadcast } : {})
        })),
        active: true
      })
      await loadBlends()
    } catch (requestError) {
      console.error('Failed to activate subcontractor blend', requestError)
      setError(requestError?.response?.data?.message || 'Unable to activate that blend right now.')
    }
  }

  const handleSubmitChemicalRequest = async () => {
    if (!requestForm.name.trim()) {
      setRequestSubmitError('Chemical name is required.')
      return
    }
    if (!requestForm.epaRegistrationNumber.trim()) {
      setRequestSubmitError('EPA registration number is required.')
      return
    }
    if (!requestForm.sdsUrl.trim()) {
      setRequestSubmitError('SDS URL is required.')
      return
    }
    if (!requestForm.labelUrl.trim()) {
      setRequestSubmitError('Label URL is required.')
      return
    }

    setSaving(true)
    setRequestSubmitError('')

    try {
      await axios.post('/api/subcontractor/chemical-requests', {
        ...requestForm,
        name: requestForm.name.trim(),
        manufacturer: requestForm.manufacturer.trim(),
        epaRegistrationNumber: requestForm.epaRegistrationNumber.trim(),
        activeIngredient: requestForm.activeIngredient.trim(),
        formulation: requestForm.formulation.trim(),
        sdsUrl: requestForm.sdsUrl.trim(),
        labelUrl: requestForm.labelUrl.trim(),
        notes: requestForm.notes.trim()
      })

      setRequestDialogOpen(false)
      resetRequestForm()
      await loadBlends()
    } catch (requestError) {
      console.error('Failed to create subcontractor chemical request', requestError)
      setRequestSubmitError(requestError?.response?.data?.message || 'Unable to submit the chemical request right now.')
    } finally {
      setSaving(false)
    }
  }

  const summaryCards = useMemo(() => ([
    {
      eyebrow: 'Vendor Blends',
      value: summary.total || items.length || 0,
      caption: 'Total blends you\'ve created.'
    },
    {
      eyebrow: 'Active',
      value: summary.active || items.filter(item => item.active !== false).length,
      caption: 'Blends ready to use.'
    },
    {
      eyebrow: 'Shared Chemicals',
      value: chemicals.length,
      caption: 'Approved chemicals available for your blends.'
    },
    {
      eyebrow: 'Inactive',
      value: Math.max((summary.total || items.length || 0) - (summary.active || items.filter(item => item.active !== false).length), 0),
      caption: 'Blends you\'ve deactivated.'
    }
  ]), [chemicals.length, items, summary])

  if (loading && !items.length && !chemicals.length) {
    return <PortalLoadingCard title="Loading blends" message="Loading your blends..." minHeight={280} />
  }

  return (
    <div>
      <Grid container spacing={3}>
        {summaryCards.map((card) => (
          <Grid item xs={12} sm={6} md={3} key={card.eyebrow}>
            <PortalMetricCard
              eyebrow={card.eyebrow}
              value={card.value}
              caption={card.caption}
            />
          </Grid>
        ))}
      </Grid>

      <Box mt={3}>
        <Card className={shared.controlsCard}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gridGap={12}>
              <div>
                <Typography variant="h6" className={shared.cardTitle}>
                  Your Blends
                </Typography>
                <Typography variant="body2" className={shared.helperText}>
                  Manage your herbicide blend recipes here. Chemicals are shared across all vendors, but your blend list is private to your team.
                </Typography>
              </div>
              <Box display="flex" gridGap={8} flexWrap="wrap">
                <Chip label={isAdmin ? 'Admin edit access' : 'Read and create access'} className={isAdmin ? 'df-chip-ok' : 'df-chip-info'} />
                <Chip label={`${chemicals.length} approved chemicals`} className="df-chip-info" />
                <Chip label={`${requestSummary.pending || 0} pending chemical request${(requestSummary.pending || 0) === 1 ? '' : 's'}`} className={(requestSummary.pending || 0) > 0 ? 'df-chip-warn' : 'df-chip-info'} />
                <Button color="primary" variant="outlined" onClick={openRequestDialog}>
                  Request New Chemical
                </Button>
                {canCreate ? (
                  <Button color="primary" variant="contained" onClick={openCreateDialog}>
                    Add Blend
                  </Button>
                ) : null}
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {error ? (
        <PortalStateCard
          title="Blends unavailable"
          message={error}
          actionLabel="Retry"
          onAction={loadBlends}
          tone="danger"
          minHeight={240}
        />
      ) : (
        <Card className={shared.tableCard}>
          <CardContent>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell className={shared.tableHeadCell}>Blend</TableCell>
                    <TableCell className={shared.tableHeadCell}>Season / Type</TableCell>
                    <TableCell className={shared.tableHeadCell}>Coverage</TableCell>
                    <TableCell className={shared.tableHeadCell}>Chemicals</TableCell>
                    <TableCell className={shared.tableHeadCell}>Updated</TableCell>
                    <TableCell className={shared.tableHeadCell} align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.length ? items.map((blend) => (
                    <TableRow key={blend._id} hover>
                      <TableCell>
                        <Typography variant="body2" className={shared.primaryText}>
                          {blend.name}
                        </Typography>
                        <Typography variant="body2" className={shared.secondaryText}>
                          {blend.active === false ? 'Inactive blend' : 'Active blend'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" className={shared.primaryText}>
                          {blend.season} / {blend.type}
                        </Typography>
                        <Typography variant="body2" className={shared.secondaryText}>
                          Calibration {blend.calibrationRate || 20} gal/acre
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <div className={classes.applicationAreaWrap}>
                          {(blend.applicationAreas || []).map((area) => (
                            <Chip key={area} label={area} size="small" className="df-chip-ok" />
                          ))}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" className={shared.primaryText}>
                          {(blend.chemicals || []).length} chemical{(blend.chemicals || []).length === 1 ? '' : 's'}
                        </Typography>
                        <Typography variant="body2" className={shared.secondaryText}>
                          {(blend.chemicals || []).slice(0, 2).map((chemical) => chemical.name).join(', ') || 'No chemical names'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {formatPortalDateTime(blend.updatedAt, { fallback: 'Not saved yet' })}
                      </TableCell>
                      <TableCell align="right">
                        <Box display="flex" justifyContent="flex-end" gridGap={8} flexWrap="wrap">
                          {isAdmin ? (
                            <>
                              <Button size="small" variant="outlined" onClick={() => openEditDialog(blend)}>
                                Edit
                              </Button>
                              {blend.active !== false ? (
                                <Button size="small" className="df-danger" onClick={() => handleDeactivate(blend)}>
                                  Deactivate
                                </Button>
                              ) : (
                                <Button size="small" color="primary" variant="contained" onClick={() => handleActivate(blend)}>
                                  Activate
                                </Button>
                              )}
                            </>
                          ) : (
                            <Tooltip title="Only subcontractor admins can edit or deactivate saved blends.">
                              <span>
                                <Button size="small" variant="outlined" disabled>
                                  Admin Only
                                </Button>
                              </span>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  )) : (
                    <TableRow>
                      <TableCell colSpan={6} align="center" className={classes.emptyCell}>
                        <Typography variant="body2" className={shared.helperText}>
                          No blends created yet. Click "Add Blend" to get started.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      <Box mt={3}>
        <Card className={shared.tableCard}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gridGap={12}>
              <div>
                <Typography variant="h6" className={shared.cardTitle}>
                  Chemical Requests
                </Typography>
                <Typography variant="body2" className={shared.helperText}>
                  Don't see a chemical you need? Submit a request and HMI will review it.
                </Typography>
              </div>
              <Box display="flex" gridGap={8} flexWrap="wrap">
                <Chip label={`Pending ${requestSummary.pending || 0}`} className={(requestSummary.pending || 0) > 0 ? 'df-chip-warn' : 'df-chip-info'} />
                <Chip label={`Approved ${requestSummary.approved || 0}`} className="df-chip-ok" />
              </Box>
            </Box>

            <Box mt={2}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell className={shared.tableHeadCell}>Chemical</TableCell>
                      <TableCell className={shared.tableHeadCell}>Manufacturer / EPA</TableCell>
                      <TableCell className={shared.tableHeadCell}>Status</TableCell>
                      <TableCell className={shared.tableHeadCell}>Requested By</TableCell>
                      <TableCell className={shared.tableHeadCell}>Submitted</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {chemicalRequests.length ? chemicalRequests.map((request) => {
                      const statusMeta = getRequestStatusMeta(request.status)

                      return (
                        <TableRow key={request._id} hover>
                          <TableCell>
                            <Typography variant="body2" className={shared.primaryText}>
                              {request.name}
                            </Typography>
                            <Typography variant="body2" className={shared.secondaryText}>
                              {request.activeIngredient || 'No active ingredient provided'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" className={shared.primaryText}>
                              {request.manufacturer || 'No manufacturer'}
                            </Typography>
                            <Typography variant="body2" className={shared.secondaryText}>
                              EPA {request.epaRegistrationNumber || 'Not provided'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip size="small" label={statusMeta.label} className={statusMeta.className} />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" className={shared.primaryText}>
                              {request.requestedByName || 'Unknown requester'}
                            </Typography>
                            <Typography variant="body2" className={shared.secondaryText}>
                              {request.reviewedAt ? `Reviewed ${formatPortalDateTime(request.reviewedAt)}` : 'Awaiting review'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {formatPortalDateTime(request.createdAt, { fallback: 'Not submitted yet' })}
                          </TableCell>
                        </TableRow>
                      )
                    }) : (
                      <TableRow>
                        <TableCell colSpan={5} align="center" className={classes.emptyCell}>
                          <Typography variant="body2" className={shared.helperText}>
                            No chemical requests submitted yet.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </CardContent>
        </Card>
      </Box>

      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editingBlend?._id ? 'Edit Blend' : 'Create Blend'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Blend Name"
                variant="outlined"
                value={form.name}
                onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                select
                fullWidth
                label="Season"
                variant="outlined"
                value={form.season}
                onChange={(event) => setForm((current) => ({ ...current, season: event.target.value }))}
              >
                {SEASON_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                select
                fullWidth
                label="Type"
                variant="outlined"
                value={form.type}
                onChange={(event) => setForm((current) => ({ ...current, type: event.target.value }))}
              >
                {TYPE_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" className={classes.rateLabel}>
                Application Areas
              </Typography>
              <Box mt={1} display="flex" flexWrap="wrap" gridGap={8}>
                {APPLICATION_AREAS.map((area) => (
                  <FormControlLabel
                    key={area}
                    control={
                      <Checkbox
                        checked={form.applicationAreas.includes(area)}
                        onChange={() => handleApplicationAreaToggle(area)}
                        color="primary"
                      />
                    }
                    label={area}
                  />
                ))}
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Calibration Rate"
                type="number"
                variant="outlined"
                value={form.calibrationRate}
                onChange={(event) => setForm((current) => ({ ...current, calibrationRate: event.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gridGap={8}>
                <Typography variant="subtitle2" className={classes.rateLabel}>
                  Chemicals
                </Typography>
                <Button
                  color="primary"
                  startIcon={<AddCircleOutline />}
                  onClick={addChemicalRow}
                >
                  Add Chemical
                </Button>
              </Box>
            </Grid>
            {form.chemicals.map((row, index) => (
              <Grid item xs={12} key={`blend-chemical-${index}`}>
                <div className={classes.chemistryBlock}>
                  <div className={classes.chemistryHeader}>
                    <Typography variant="subtitle2" className={classes.rateLabel}>
                      Chemical #{index + 1}
                    </Typography>
                    {form.chemicals.length > 1 ? (
                      <IconButton size="small" onClick={() => removeChemicalRow(index)}>
                        <DeleteOutline />
                      </IconButton>
                    ) : null}
                  </div>

                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        select
                        fullWidth
                        label="Chemical"
                        variant="outlined"
                        value={row.chemicalId}
                        onChange={(event) => updateChemicalRow(index, { chemicalId: event.target.value })}
                      >
                        {chemicals.map((chemical) => (
                          <MenuItem key={chemical._id} value={chemical._id}>
                            {buildChemicalOptionLabel(chemical)}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>

                    <Grid item xs={12}>
                      <Typography variant="body2" className={classes.rateLabel}>
                        Spot Rate
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Spot Rate"
                        type="number"
                        variant="outlined"
                        value={row.spotRate}
                        onChange={(event) => updateChemicalRow(index, { spotRate: event.target.value })}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        select
                        fullWidth
                        label="Unit"
                        variant="outlined"
                        value={row.spotUnit}
                        onChange={(event) => updateChemicalRow(index, { spotUnit: event.target.value })}
                      >
                        {RATE_UNITS.map((option) => (
                          <MenuItem key={option} value={option}>
                            {option}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        select
                        fullWidth
                        label="Per"
                        variant="outlined"
                        value={row.spotPer}
                        onChange={(event) => updateChemicalRow(index, { spotPer: event.target.value })}
                      >
                        {RATE_PERS.map((option) => (
                          <MenuItem key={option} value={option}>
                            {option}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>

                    <Grid item xs={12}>
                      <Typography variant="body2" className={classes.rateLabel}>
                        Broadcast Rate
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Broadcast Rate"
                        type="number"
                        variant="outlined"
                        value={row.broadcastRate}
                        onChange={(event) => updateChemicalRow(index, { broadcastRate: event.target.value })}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        select
                        fullWidth
                        label="Unit"
                        variant="outlined"
                        value={row.broadcastUnit}
                        onChange={(event) => updateChemicalRow(index, { broadcastUnit: event.target.value })}
                      >
                        {RATE_UNITS.map((option) => (
                          <MenuItem key={option} value={option}>
                            {option}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        select
                        fullWidth
                        label="Per"
                        variant="outlined"
                        value={row.broadcastPer}
                        onChange={(event) => updateChemicalRow(index, { broadcastPer: event.target.value })}
                      >
                        {RATE_PERS.map((option) => (
                          <MenuItem key={option} value={option}>
                            {option}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  </Grid>
                </div>
              </Grid>
            ))}
            {submitError ? (
              <Grid item xs={12}>
                <Typography variant="body2" color="error">
                  {submitError}
                </Typography>
              </Grid>
            ) : null}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={saving}>
            Cancel
          </Button>
          <Button color="primary" variant="contained" onClick={handleSubmit} disabled={saving}>
            {saving ? 'Saving...' : (editingBlend?._id ? 'Save Blend' : 'Create Blend')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={requestDialogOpen} onClose={closeRequestDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Request New Chemical</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Chemical Name"
                variant="outlined"
                value={requestForm.name}
                onChange={(event) => setRequestForm((current) => ({ ...current, name: event.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Manufacturer"
                variant="outlined"
                value={requestForm.manufacturer}
                onChange={(event) => setRequestForm((current) => ({ ...current, manufacturer: event.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="EPA Registration Number"
                variant="outlined"
                value={requestForm.epaRegistrationNumber}
                onChange={(event) => setRequestForm((current) => ({ ...current, epaRegistrationNumber: event.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Active Ingredient"
                variant="outlined"
                value={requestForm.activeIngredient}
                onChange={(event) => setRequestForm((current) => ({ ...current, activeIngredient: event.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Formulation"
                variant="outlined"
                value={requestForm.formulation}
                onChange={(event) => setRequestForm((current) => ({ ...current, formulation: event.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="SDS URL"
                variant="outlined"
                value={requestForm.sdsUrl}
                onChange={(event) => setRequestForm((current) => ({ ...current, sdsUrl: event.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Label URL"
                variant="outlined"
                value={requestForm.labelUrl}
                onChange={(event) => setRequestForm((current) => ({ ...current, labelUrl: event.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={4}
                label="Notes"
                variant="outlined"
                value={requestForm.notes}
                onChange={(event) => setRequestForm((current) => ({ ...current, notes: event.target.value }))}
                helperText="Use this for target weeds, intended use, or any extra review context."
              />
            </Grid>
            {requestSubmitError ? (
              <Grid item xs={12}>
                <Typography variant="body2" color="error">
                  {requestSubmitError}
                </Typography>
              </Grid>
            ) : null}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRequestDialog} disabled={saving}>
            Cancel
          </Button>
          <Button color="primary" variant="contained" onClick={handleSubmitChemicalRequest} disabled={saving}>
            {saving ? 'Submitting...' : 'Submit Request'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

const mapStateToProps = (state) => ({
  user: state.user
})

export default connect(mapStateToProps)(SubcontractorBlends)
